import { useContext, useState } from 'react'
import { ThemeContext } from '../../hooks/useTheme'

interface ThemeToggleProps {
  variant?: 'header' | 'sidebar' | 'default'
  label?: string
}

function readDocumentTheme(): 'light' | 'dark' {
  if (typeof document !== 'undefined') {
    const t = document.documentElement.dataset.theme
    if (t === 'dark' || t === 'light') return t
  }
  return 'light'
}

export function ThemeToggle({ variant = 'default', label }: ThemeToggleProps) {
  const ctx = useContext(ThemeContext)
  // Graceful fallback for isolated unit tests / stories without provider.
  const [fallback, setFallback] = useState<'light' | 'dark'>(() => readDocumentTheme())
  const theme = ctx ? ctx.theme : fallback
  const isDark = theme === 'dark'

  const toggle = () => {
    if (ctx) {
      ctx.toggleTheme()
      return
    }
    const next = fallback === 'light' ? 'dark' : 'light'
    setFallback(next)
    try {
      document.documentElement.dataset.theme = next
      document.documentElement.style.colorScheme = next
      window.localStorage.setItem('wg-theme', next)
    } catch {
      // ignore
    }
  }

  const className =
    variant === 'header'
      ? 'theme-toggle theme-toggle-header'
      : variant === 'sidebar'
        ? 'theme-toggle theme-toggle-sidebar'
        : 'theme-toggle'

  return (
    <button
      type="button"
      onClick={toggle}
      className={className}
      aria-label={isDark ? 'Aktifkan mode terang' : 'Aktifkan mode gelap'}
      aria-pressed={isDark}
      title={isDark ? 'Mode terang' : 'Mode gelap'}
    >
      <span aria-hidden="true" className="theme-toggle-track">
        {isDark ? (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
            />
          </svg>
        ) : (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
            />
          </svg>
        )}
      </span>
      {label ? <span className="theme-toggle-label">{label}</span> : null}
    </button>
  )
}
